import {
  CheckCircle,
  ExternalLink,
  Eye,
  Linkedin,
  Image as LucideImage,
  MessageCircle,
  Share,
  ThumbsUp,
  XCircle,
} from 'lucide-react'
import Image from 'next/image'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { linkedinCreatePost } from 'sprites-types/workflow/tools/linkedinCreatePost'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function LinkedinCreatePostDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(linkedinCreatePost, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data
  const inputs = nodeExecution.inputs

  // Extract post type and params
  const postType = inputs.postType || 'text'
  const params = inputs.params || inputs

  // Extract text content
  const text = params.text || inputs.text || params.content || inputs.content || ''

  // Extract image URL if it's an image post
  const imageUrl =
    params.imageUrl ||
    inputs.imageUrl ||
    params.image ||
    inputs.image ||
    params.file ||
    inputs.file ||
    ''

  // Extract result data
  const postUrl =
    (result as any)?.response?.recentActivityUrl ||
    (result as any)?.recentActivityUrl ||
    (result as any)?.url ||
    (result as any)?.postUrl ||
    (result as any)?.link ||
    ''
  const postId = (result as any)?.id || (result as any)?.postId || ''
  const summary = (result as any)?.response?.summary || (result as any)?.summary || ''
  const success = (result as any)?.success !== false && (postUrl || postId || summary)
  const error = (result as any)?.error || (result as any)?.message || ''
  const visibility = params.visibility || inputs.visibility || 'PUBLIC'

  const formatVisibility = (vis: string) => {
    switch (vis.toUpperCase()) {
      case 'PUBLIC':
        return 'Public'
      case 'CONNECTIONS':
        return 'Connections only'
      case 'LOGGED_IN':
        return 'LinkedIn members'
      default:
        return vis
    }
  }

  const isImagePost = postType === 'image' || imageUrl

  return (
    <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
      <div className="px-4 py-3">
        <div className="flex items-start gap-4">
          <div className="mt-1 flex-shrink-0">
            <div className="rounded-full bg-blue-100 p-2">
              {success ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : (
                <XCircle className="h-5 w-5 text-red-600" />
              )}
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="mb-2 flex items-center gap-2">
              <Linkedin className="h-4 w-4 text-blue-600" />
              <div className="text-sm font-semibold text-gray-900">
                {success ? 'LinkedIn Post Created' : 'LinkedIn Post Failed'}
              </div>
              {success && <CheckCircle className="h-4 w-4 text-green-600" />}
            </div>

            <div className="mb-3 flex items-center gap-3 text-xs text-gray-600">
              {isImagePost && (
                <div className="flex items-center gap-1">
                  <LucideImage className="h-3 w-3" />
                  <span>Image Post</span>
                </div>
              )}
              <div className="flex items-center gap-1">
                <Eye className="h-3 w-3" />
                <span>{formatVisibility(visibility)}</span>
              </div>
              {postId && <div>ID: {postId}</div>}
            </div>

            {error && !success && (
              <div className="mb-3 rounded-2xl bg-red-50 p-2 text-xs text-red-600">{error}</div>
            )}

            {summary && (
              <div className="mb-3 rounded-2xl bg-green-50 p-2 text-xs text-green-700">
                {summary}
              </div>
            )}

            {text && (
              <div className="mb-3">
                <div className="mb-2 text-xs font-medium text-gray-700">Post Content:</div>
                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-3">
                  <div className="whitespace-pre-wrap text-xs text-gray-800">
                    {text.length > 280 ? `${text.slice(0, 280)}...` : text}
                  </div>
                </div>
              </div>
            )}

            {imageUrl && (
              <div className="mb-3">
                <div className="mb-2 text-xs font-medium text-gray-700">Image:</div>
                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-2">
                  <Image
                    src={imageUrl}
                    alt="LinkedIn post image"
                    width={400}
                    height={128}
                    className="h-32 w-full rounded-xl object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none'
                      ;(e.currentTarget.nextElementSibling! as HTMLElement).style.display = 'block'
                    }}
                  />
                  <div className="hidden py-4 text-center text-xs text-gray-500">
                    <LucideImage className="mx-auto mb-1 h-4 w-4" />
                    Image not available
                  </div>
                </div>
              </div>
            )}

            {success && (
              <div className="flex items-center gap-4 text-xs text-gray-600">
                <div className="flex items-center gap-1">
                  <ThumbsUp className="h-3 w-3" />
                  <span>0 likes</span>
                </div>
                <div className="flex items-center gap-1">
                  <MessageCircle className="h-3 w-3" />
                  <span>0 comments</span>
                </div>
                <div className="flex items-center gap-1">
                  <Share className="h-3 w-3" />
                  <span>0 shares</span>
                </div>
              </div>
            )}

            {postUrl && (
              <div className="mt-3 border-t border-gray-200 pt-2">
                <a
                  href={postUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                >
                  <ExternalLink className="h-3 w-3" />
                  View on LinkedIn
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function downloadLinkedinCreatePost(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function linkedinCreatePostToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(linkedinCreatePost, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data
  const inputs = nodeExecution.inputs

  const postType = inputs.postType || 'text'
  const params = inputs.params || inputs

  const text = params.text || inputs.text || params.content || inputs.content || ''
  const imageUrl =
    params.imageUrl ||
    inputs.imageUrl ||
    params.image ||
    inputs.image ||
    params.file ||
    inputs.file ||
    ''
  const postUrl =
    (result as any)?.response?.recentActivityUrl ||
    (result as any)?.recentActivityUrl ||
    (result as any)?.url ||
    (result as any)?.postUrl ||
    ''
  const postId = (result as any)?.id || (result as any)?.postId || ''
  const summary = (result as any)?.response?.summary || (result as any)?.summary || ''
  const success = (result as any)?.success !== false && (postUrl || postId || summary)
  const error = (result as any)?.error || (result as any)?.message || ''
  const visibility = params.visibility || inputs.visibility || 'PUBLIC'

  return `## LinkedIn: Create Post

**Status:** ${success ? '✅ Success' : '❌ Failed'}
**Post Type:** ${postType === 'image' ? 'Image Post' : 'Text Post'}
${postId ? `**Post ID:** ${postId}` : ''}
**Visibility:** ${visibility}
${error ? `**Error:** ${error}` : ''}
${summary ? `**Summary:** ${summary}` : ''}

${
  text
    ? `**Post Text:**
${text}

`
    : ''
}${
    imageUrl
      ? `**Image URL:** ${imageUrl}

`
      : ''
  }${postUrl ? `**LinkedIn URL:** ${postUrl}` : ''}

---
`
}

export const LinkedinCreatePostDisplay = createWorkflowToolComponent(
  LinkedinCreatePostDisplayComponent,
  downloadLinkedinCreatePost,
  linkedinCreatePostToMarkdown
)
